if ( typeof VideoAutoplay !== 'function' ) {

  // Shared across every <video-autoplay> instance on the page, so a busy grid never
  // fires off a burst of simultaneous video requests that would compete for bandwidth
  // and main-thread time with the page's real LCP/critical-path resources.
  const _queue = [];
  let _active = 0;
  let _pageLoaded = ( document.readyState === 'complete' );

  function _maxConcurrent() {
    // Stricter on narrow/mobile viewports, where bandwidth and CPU are the tightest.
    return window.innerWidth >= 1024 ? 3 : 1;
  }

  function _processQueue() {
    while ( _active < _maxConcurrent() && _queue.length ) {
      const next = _queue.shift();
      if ( next.isConnected && next._wantsPlay ) {
        next._grantSlot();
      }
    }
  }

  if ( ! _pageLoaded ) {
    window.addEventListener('load', () => {
      _pageLoaded = true;
      // Give the browser a beat after `load` to settle hero image decode/fonts/etc.
      // before any autoplay video starts competing for the network or main thread.
      const kick = () => _processQueue();
      if ( 'requestIdleCallback' in window ) {
        requestIdleCallback(kick, { timeout: 1500 });
      } else {
        setTimeout(kick, 300);
      }
    }, { once: true });
  }

  class VideoAutoplay extends HTMLElement {

    constructor() {
      super();
      this._loaded = false;
      this._hasSlot = false;
      this._wantsPlay = false;
      this._frame = null;
    }

    connectedCallback() {

      // Respect data-saver preference only: never fetch or play video on a metered/slow
      // connection, keep the poster image only. (Video always autoplays regardless of the
      // visitor's OS-level "reduce motion" setting — a deliberate choice for this store.)
      const saveData = navigator.connection && ( navigator.connection.saveData || /2g/.test(navigator.connection.effectiveType || '') );
      if ( saveData ) {
        this.classList.add('static-fallback');
        return;
      }

      this._video = this.querySelector('video');
      this._frameHolder = this.querySelector('.media-autoplay-video__frame');

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if ( entry.isIntersecting ) {
            this._enterViewport();
          } else {
            this._exitViewport();
          }
        });
      }, {
        threshold: [0, 0.25],
        rootMargin: '200px 0px 200px 0px'
      });

      this.observer.observe(this);

    }

    _enterViewport() {

      this._wantsPlay = true;

      if ( this._hasSlot ) {
        this._resume();
        return;
      }

      if ( ! _pageLoaded || _active >= _maxConcurrent() ) {
        if ( _queue.indexOf(this) === -1 ) {
          _queue.push(this);
        }
        return;
      }

      this._grantSlot();

    }

    _exitViewport() {

      this._wantsPlay = false;

      const queueIndex = _queue.indexOf(this);
      if ( queueIndex > -1 ) {
        _queue.splice(queueIndex, 1);
      }

      if ( this._hasSlot ) {
        this._pause();
        this._hasSlot = false;
        _active--;
        _processQueue();
      }

    }

    _grantSlot() {

      this._hasSlot = true;
      _active++;

      if ( ! this._loaded ) {
        this._loaded = true;
        this._load();
      } else {
        this._resume();
      }

    }

    _resume() {
      if ( this._video && this._video.paused ) {
        // iOS Safari can silently ignore play() unless muted/playsInline are set
        // as JS properties (not just HTML attributes) right before the call.
        this._video.muted = true;
        this._video.playsInline = true;
        this._video.play().catch(() => {});
      } else if ( this._frame ) {
        this._postMessage(this.dataset.mediaType === 'external_video-vimeo' ? '{"method":"play"}' : '{"event":"command","func":"playVideo","args":""}');
      }
    }

    _pause() {
      if ( this._video && ! this._video.paused ) {
        this._video.pause();
      } else if ( this._frame ) {
        this._postMessage(this.dataset.mediaType === 'external_video-vimeo' ? '{"method":"pause"}' : '{"event":"command","func":"pauseVideo","args":""}');
      }
    }

    _load() {

      if ( this._video ) {

        // iOS Safari requires muted/playsInline as JS properties (not just HTML
        // attributes) to be set before play() is called, or it silently refuses to autoplay.
        this._video.muted = true;
        this._video.playsInline = true;

        this._video.addEventListener('error', () => {
          this.classList.add('static-fallback');
        });
        this._video.addEventListener('playing', () => {
          this.classList.add('loaded');
        });

        const source = this._video.dataset.src;
        if ( source ) {
          this._video.src = source;
          this._video.load();
          const attemptPlay = () => this._video.play().catch(() => {});
          // iOS sometimes rejects an immediate play() call before the video is
          // actually ready; retry once it reports it has enough data.
          this._video.addEventListener('loadedmetadata', attemptPlay, { once: true });
          attemptPlay();
        }

      } else if ( this._frameHolder && this._frameHolder.dataset.embedSrc ) {

        this._frame = document.createElement('iframe');
        this._frame.src = this._frameHolder.dataset.embedSrc;
        this._frame.setAttribute('allow', 'autoplay; encrypted-media');
        this._frame.setAttribute('frameborder', '0');
        this._frame.setAttribute('tabindex', '-1');
        this._frame.addEventListener('load', () => {
          this.classList.add('loaded');
        });
        this._frameHolder.appendChild(this._frame);

      }

    }

    _postMessage(message) {
      if ( this._frame && this._frame.contentWindow ) {
        this._frame.contentWindow.postMessage(message, '*');
      }
    }

    disconnectedCallback() {

      if ( this.observer ) {
        this.observer.disconnect();
      }

      const queueIndex = _queue.indexOf(this);
      if ( queueIndex > -1 ) {
        _queue.splice(queueIndex, 1);
      }

      if ( this._hasSlot ) {
        this._hasSlot = false;
        _active--;
        _processQueue();
      }

    }

  }

  if ( typeof customElements.get('video-autoplay') == 'undefined' ) {
    customElements.define('video-autoplay', VideoAutoplay);
  }

}
